// WebAuthn helper functions for passkey registration and authentication

// Convert base64url to ArrayBuffer
function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(base64)
  const buffer = new ArrayBuffer(binary.length)
  const bytes = new Uint8Array(buffer)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return buffer
}

// Convert ArrayBuffer to base64url
function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  const base64 = btoa(binary)
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

// Prepare credential creation options
function prepareCreationOptions(options) {
  return {
    ...options,
    challenge: base64urlToBuffer(options.challenge),
    user: {
      ...options.user,
      id: base64urlToBuffer(options.user.id)
    },
    excludeCredentials: options.excludeCredentials?.map(cred => ({
      ...cred,
      id: base64urlToBuffer(cred.id)
    })) || []
  }
}

// Prepare credential request options
function prepareRequestOptions(options) {
  return {
    ...options,
    challenge: base64urlToBuffer(options.challenge),
    allowCredentials: options.allowCredentials?.map(cred => ({
      ...cred,
      id: base64urlToBuffer(cred.id)
    })) || []
  }
}

// Encode credential for server
function encodeCredential(credential) {
  const response = credential.response
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      attestationObject: bufferToBase64url(response.attestationObject)
    }
  }
}

// Encode assertion for server
function encodeAssertion(credential) {
  const response = credential.response
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      authenticatorData: bufferToBase64url(response.authenticatorData),
      signature: bufferToBase64url(response.signature),
      userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : null
    }
  }
}

// Register a new passkey
// Accepts optional userId parameter for nested routes
window.registerPasskey = async function(nickname = '', userId = null) {
  try {
    // Determine the correct API endpoint
    const newPath = userId ? `/settings/users/${userId}/passkeys/new` : '/webauthn_credentials/new'
    const createPath = userId ? `/settings/users/${userId}/passkeys` : '/webauthn_credentials'

    // Get creation options from server
    const optionsResponse = await fetch(newPath, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      }
    })

    if (!optionsResponse.ok) {
      throw new Error('Failed to get registration options')
    }

    const options = await optionsResponse.json()
    const publicKeyOptions = prepareCreationOptions(options.publicKey)

    // Create credential
    const credential = await navigator.credentials.create({
      publicKey: publicKeyOptions
    })

    if (!credential) {
      throw new Error('Failed to create credential')
    }

    // Send credential to server
    const encodedCredential = encodeCredential(credential)
    const createResponse = await fetch(createPath, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify({
        ...encodedCredential,
        nickname
      })
    })

    const result = await createResponse.json()

    if (!createResponse.ok) {
      throw new Error(result.error || 'Failed to register passkey')
    }

    return result
  } catch (error) {
    console.error('Passkey registration error:', error)
    throw error
  }
}

// `offerOtherDevices` tells the caller that asking again with the usual
// dialog may still find a passkey: the browser never looked beyond this device
// (immediate UI mode), or the passkey it found is one CGX no longer knows.
function passkeyUnavailableError(cause, offerOtherDevices) {
  const error = new Error('No passkey available for this relying party', { cause })
  error.name = 'PasskeyUnavailableError'
  error.offerOtherDevices = offerOtherDevices
  return error
}

// Asks the browser's password manager to drop a passkey CGX has no record of,
// e.g. one deleted under "Passkeys verwalten", so it stops offering it.
function forgetUnknownPasskey(rpId, credentialId) {
  if (typeof window.PublicKeyCredential?.signalUnknownCredential !== 'function') return

  window.PublicKeyCredential.signalUnknownCredential({ rpId, credentialId }).catch(() => {})
}

// Immediate UI mode needs a user gesture that is still active: a slow
// challenge request can outlast it, and the browser would then report "no
// passkey" for a user who has one, so ask with the usual dialog instead.
async function supportsImmediateGet() {
  if (navigator.userActivation && !navigator.userActivation.isActive) return false
  if (typeof window.PublicKeyCredential?.getClientCapabilities !== 'function') return false

  try {
    const capabilities = await window.PublicKeyCredential.getClientCapabilities()
    return capabilities.immediateGet === true
  } catch (error) {
    return false
  }
}

function isNoPasskeyForRelyingParty(error) {
  // credentials.get() maps "no matching passkey for this RP ID", an empty
  // picker, and a dismissed picker to NotAllowedError / InvalidStateError.
  return error.name === 'NotAllowedError' || error.name === 'InvalidStateError'
}

// Authenticate with passkey
// If email is provided, uses email-based auth; otherwise uses usernameless auth.
// By default the browser is asked in immediate UI mode where it supports it;
// pass immediate: false to get the usual dialog, which also offers a passkey
// on another device (phone via QR code) or on a security key.
window.authenticateWithPasskey = async function(email = null, { immediate = true } = {}) {
  try {
    // Get challenge from server
    const body = email ? JSON.stringify({ email }) : JSON.stringify({})

    const challengeResponse = await fetch('/webauthn_sessions/challenge', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      },
      body
    })

    if (!challengeResponse.ok) {
      const error = await challengeResponse.json()
      throw new Error(error.error || 'Failed to get authentication challenge')
    }

    const options = await challengeResponse.json()
    const publicKeyOptions = prepareRequestOptions(options.publicKey)

    // Immediate UI mode fails without a picker when this device holds no
    // passkey for this RP ID. It never offers a passkey on a phone or a
    // security key, and always fails in a private window, so a failure in it
    // does not mean the user has no passkey at all.
    const useImmediate = immediate && await supportsImmediateGet()
    const getRequest = { publicKey: publicKeyOptions }
    if (useImmediate) {
      getRequest.uiMode = 'immediate'
    }

    let credential
    try {
      credential = await navigator.credentials.get(getRequest)
    } catch (error) {
      if (isNoPasskeyForRelyingParty(error)) {
        throw passkeyUnavailableError(error, useImmediate)
      }
      throw error
    }

    if (!credential) {
      throw passkeyUnavailableError(null, useImmediate)
    }

    // Send assertion to server
    const encodedAssertion = encodeAssertion(credential)
    const authResponse = await fetch('/webauthn_sessions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: JSON.stringify(encodedAssertion)
    })

    const result = await authResponse.json()

    if (!authResponse.ok) {
      if (result.error_code === 'unknown_credential') {
        forgetUnknownPasskey(publicKeyOptions.rpId, credential.id)
        throw passkeyUnavailableError(null, true)
      }
      throw new Error(result.error || 'Authentication failed')
    }

    return result
  } catch (error) {
    if (error.name !== 'PasskeyUnavailableError') {
      console.error('Passkey authentication error:', error)
    }
    throw error
  }
}

window.isPasskeyUnavailableError = function(error) {
  return error?.name === 'PasskeyUnavailableError'
}

// Check if WebAuthn is supported
window.isWebAuthnSupported = function() {
  return typeof window.PublicKeyCredential !== 'undefined' &&
         typeof navigator.credentials !== 'undefined' &&
         typeof navigator.credentials.create === 'function'
}
